import { useMutation } from '@apollo/client'
import { Checkbox } from 'antd'

import { Button } from './shared-components/BUI/Button'
import { Form } from './shared-components/BUI/Form'
import { Modal } from './shared-components/BUI/Modal'
import { Flexbox } from './shared-components/Flexbox'

import { useLocale } from '@/context/localeContext'
import { useNavigation } from '@/context/navigationContext'
import { useUser } from '@/context/userContext'
import { UPDATE_USER_SETTINGS } from '@/modules/settings/graphQL'
import { NavGroup, NavItem } from '@/utils/navigationConfig'

import {
  UpdateUserSettingsUiMutation,
  UpdateUserSettingsUiMutationVariables,
} from 'types/api.graphql'

enum Field {
  Favourites = 'favourites',
}

type FormFieldValues = {
  [Field.Favourites]: string[]
}

type Props = {
  open: boolean
  closeModal: () => void
}

const flattenNavItems = (navGroups: NavGroup[]) => {
  const extractItems = (items: NavItem[], depth: number): NavItem[] => {
    return items.reduce((acc, item: NavItem) => {
      const mappedItem = { ...item, depth }
      acc.push(mappedItem)
      if (mappedItem.subItems && mappedItem.subItems.length > 0) {
        acc.push(...extractItems(mappedItem.subItems, depth + 1))
      }
      return acc
    }, [])
  }

  return navGroups.reduce((acc, group: NavGroup) => {
    acc.push(...extractItems(group.items, 0))
    return acc
  }, [])
}

export const FavouritesSelectModal: React.FC<Props> = ({
  open,
  closeModal,
}) => {
  const { userLoading, settings, refetchUser } = useUser()
  const { navigationConfig } = useNavigation()
  const { t } = useLocale()
  const [form] = Form.useForm()

  const [updateUserSettings, { loading }] = useMutation<
    UpdateUserSettingsUiMutation,
    UpdateUserSettingsUiMutationVariables
  >(UPDATE_USER_SETTINGS)

  const selected = settings?.favourites ?? []

  if (userLoading || !navigationConfig) return null

  const onSubmit = async (values: FormFieldValues) => {
    await updateUserSettings({
      variables: { input: { favourites: values.favourites ?? [] } },
    })
    refetchUser()
    closeModal()
  }

  const checkboxOptions = flattenNavItems(
    navigationConfig.filter((category) => category.id !== 'favourites')
  )

  return (
    <Modal
      title={t('common:navigation.favouritesHeader')}
      open={open}
      onCancel={closeModal}
      modalRender={(children) => (
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          initialValues={{ [Field.Favourites]: selected }}
        >
          {children}
        </Form>
      )}
      footerLayout="split"
      footer={
        <>
          <Button onClick={closeModal}>Cancel</Button>
          <Button onClick={() => form.setFieldValue(Field.Favourites, [])}>
            Unselect all
          </Button>
          <Button type="submit" variant="primary" loading={loading}>
            Save
          </Button>
        </>
      }
    >
      <Form.Item name={Field.Favourites}>
        <Checkbox.Group style={{ width: '100%' }}>
          <Flexbox
            flexDirection="column"
            gap="0.5rem"
            style={{
              maxHeight: '350px',
              overflowY: 'scroll',
            }}
          >
            {checkboxOptions.map((option) => (
              <Checkbox
                key={'fav-modal-' + option.id}
                value={option.id}
                style={{ marginLeft: option.depth * 20 }}
              >
                {t(option.title)}
              </Checkbox>
            ))}
          </Flexbox>
        </Checkbox.Group>
      </Form.Item>
    </Modal>
  )
}
